// Frontend audit helper. API calls are logged automatically by backend middleware;
// this module is for events that don't naturally hit an API — page views and explicit UI actions.
//
// Failures are silent: audit must never break a page render.

import { getUser } from '../auth/authService';

const ANALYTICS_URL = process.env.REACT_APP_ANALYTICS_MICROSERVICE_URL;

const send = async (body) => {
    if (!ANALYTICS_URL) return;
    try {
        const user = await getUser();
        if (!user?.access_token) return;
        await fetch(`${ANALYTICS_URL}/audit/log`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${user.access_token}`,
            },
            body: JSON.stringify(body),
            keepalive: true, // survive page unload
        });
    } catch {
        // swallow — audit is best-effort
    }
};

// The referrer's query string is never recorded: after sign-in it is the OIDC
// callback URL, whose `code=` is an authorization code (seen in prod audit
// rows 2026-09-24). Origin + path is all the trail needs.
export const safeReferrer = (ref = document.referrer) => {
    if (!ref) return '';
    try {
        const u = new URL(ref);
        return u.origin + u.pathname;
    } catch {
        return String(ref).split('?')[0].split('#')[0];
    }
};

export const logPageView = (path) => send({
    actionType: 'page_view',
    resource: path,
    details: { referrer: safeReferrer() },
});

export const logAction = (actionType, resource, details = {}) => send({
    actionType,
    resource,
    details,
});

// ---------------------------------------------------------------------------
// Queued events — UI interactions and work actions.
//
// One request per click would be a request per click, so events queue and go
// out together to POST /audit/log/batch: every few seconds, when the queue
// fills, when a work action lands (those should not wait), and when the tab is
// hidden or closed. If the batch endpoint is not there (an older analytics
// deploy) each event falls back to the single-event endpoint.
// ---------------------------------------------------------------------------

const FLUSH_MS = 4000;
const FLUSH_AT = 25;
const KEEPALIVE_BYTES = 60000; // browsers cap keepalive bodies at 64KB

let queue = [];
let timer = null;
let batchMissing = false;

export const flushQueue = async () => {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!queue.length || !ANALYTICS_URL) { queue = []; return; }
    const events = queue;
    queue = [];
    try {
        const user = await getUser();
        if (!user?.access_token) return;
        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${user.access_token}` };
        if (!batchMissing) {
            const body = JSON.stringify({ events });
            const res = await fetch(`${ANALYTICS_URL}/audit/log/batch`, {
                method: 'POST', headers, body, keepalive: body.length < KEEPALIVE_BYTES,
            });
            if (res.status !== 404 && res.status !== 405) return;
            batchMissing = true;
        }
        await Promise.all(events.map((e) => fetch(`${ANALYTICS_URL}/audit/log`, {
            method: 'POST', headers, body: JSON.stringify(e), keepalive: true,
        }).catch(() => {})));
    } catch {
        // swallow — audit is best-effort
    }
};

export const queueEvent = (actionType, resource, details = {}, { now = false } = {}) => {
    queue.push({ actionType, resource, details, at: new Date().toISOString() });
    if (now || queue.length >= FLUSH_AT) { flushQueue(); return; }
    if (!timer) timer = setTimeout(flushQueue, FLUSH_MS);
};

if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushQueue();
    });
    window.addEventListener('pagehide', () => { flushQueue(); });
}

/** Test seam. */
export const _pendingEvents = () => queue;
